/**
 * ITER304 · Non-member Gift Voucher landing page.
 *
 * Route: /loyalty/gift/:token   (public — no auth)
 *
 * The recipient of a gift voucher lands here after tapping the
 * shared link.  Shows:
 *   • the designed voucher card (via <VoucherCard>)
 *   • the sender's personalised message
 *   • recipient name
 *   • "Redeem at counter" instructions
 *
 * Redemption itself happens at the outlet via the existing EDC-OTP
 * flow — this page just gives the recipient the code to present.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Copy, Sparkles } from 'lucide-react';
import VoucherCard from '../components/loyalty/VoucherCard';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GiftVoucherLanding() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState('');

  useEffect(() => {
    axios.get(`${API}/api/loyalty/gift/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Gift not found'));
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6"
           data-testid="gift-landing-error">
        <div className="text-center text-slate-300 max-w-sm">
          <Gift className="w-8 h-8 mx-auto mb-3 opacity-40" />
          <div className="font-serif text-2xl mb-2">Gift not found</div>
          <div className="text-sm opacity-70">
            The link may be invalid or the gift has been removed.
          </div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-500 text-sm">
        Loading…
      </div>
    );
  }

  const { gift, voucher_type, outlet } = data;
  const outletInitials = (outlet.name || 'RP')
    .split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(gift.code);
      toast.success('Code copied to clipboard');
    } catch { toast.info('Please copy the code manually'); }
  };

  const stateFor = gift.status === 'redeemed' ? 'used' : 'available';
  const validUntil = gift.valid_until
    ? new Date(gift.valid_until).toLocaleDateString('en-IN',
        { day: '2-digit', month: 'short', year: 'numeric' })
    : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950
                    text-slate-100 px-4 py-8"
         data-testid="gift-landing-page">
      <div className="max-w-md mx-auto">
        {/* Header — sender / recipient */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase
                          tracking-[0.22em] text-amber-400/80 mb-2">
            <Sparkles className="w-3 h-3" /> A gift for you
          </div>
          {gift.recipient_name && (
            <div className="font-serif text-3xl mb-1"
                 data-testid="gift-recipient">
              For {gift.recipient_name}
            </div>
          )}
          {gift.sender_name && (
            <div className="text-[12px] text-slate-400">
              from <span className="text-slate-200">{gift.sender_name}</span>
            </div>
          )}
        </div>

        {/* Personalised message */}
        {gift.message && (
          <blockquote
            className="mb-6 border-l-2 border-amber-400/60 pl-4 py-1
                       text-[13.5px] italic text-slate-300"
            data-testid="gift-message"
          >
            {gift.message}
          </blockquote>
        )}

        {/* The card itself */}
        <div className="flex justify-center mb-6">
          <VoucherCard
            voucher={{
              name:          voucher_type.name,
              subtitle:      voucher_type.description,
              benefit_type:  voucher_type.benefit_type,
              benefit_value: voucher_type.benefit_value,
              code:          gift.code,
            }}
            design={voucher_type.design}
            outletName={outlet.name}
            outletTagline={outlet.tagline}
            outletLogo={outlet.logo_url}
            outletInitials={outletInitials}
            validUntil={validUntil}
            state={stateFor}
            size="phone"
          />
        </div>

        {/* Actions */}
        {stateFor === 'available' ? (
          <>
            <button
              onClick={copyCode}
              data-testid="gift-copy-code"
              className="w-full h-11 rounded-md font-semibold text-slate-950
                         bg-amber-400 hover:bg-amber-300 inline-flex items-center
                         justify-center gap-2 shadow"
            >
              <Copy className="w-4 h-4" />
              Copy Voucher Code
            </button>
            <div className="mt-4 text-[11.5px] text-slate-400 leading-relaxed
                            text-center">
              Show this card (or the code) at <span className="text-slate-200 font-medium">
              {outlet.name}</span> to redeem your gift.
              {validUntil && <> · Valid until {validUntil}.</>}
            </div>
          </>
        ) : (
          <div className="text-center text-[13px] text-slate-400"
               data-testid="gift-redeemed-note">
            This gift has already been redeemed.
          </div>
        )}
      </div>
    </div>
  );
}
